import type { Rect } from "./matrix";
import { parseCmds, segmentPoint, VERB, type Pt } from "./pathcmds";
import type { WarpFn } from "./pathwarp";

/**
 * Puppet warp (SPEC.md §5): the node's silhouette is triangulated into a mesh, pins drag it
 * around, and the mesh deforms as-rigidly-as-possible (Igarashi, Moscovich & Hughes,
 * "As-Rigid-As-Possible Shape Manipulation", SIGGRAPH 2005 — the method Photoshop's Puppet
 * Warp is based on). Both solves are linear least squares whose matrices only depend on the
 * mesh and on which pins exist, so they're factorised once per pin set and each drag frame is
 * just back-substitution.
 */

export type PuppetDensity = "fewer" | "normal" | "more";
export type PuppetMode = "rigid" | "normal" | "distort";

/** Approximate vertex budget per density (over the silhouette's area). */
const DENSITY_TARGET: Record<PuppetDensity, number> = { fewer: 300, normal: 800, more: 1900 };

/** How much of each triangle's own scale change the final pass keeps (0 = rigid). */
const MODE_SCALE: Record<PuppetMode, number> = { rigid: 0, normal: 0.4, distort: 1 };

// ------------------------------------------------------------------ silhouette masks

/** Coverage on a regular grid of `px` × `py` pixels whose top-left is (x, y), node-local. */
export interface SilhouetteMask {
  x: number;
  y: number;
  px: number;
  py: number;
  cols: number;
  rows: number;
  data: Uint8Array;
}

export function emptyMask(r: Rect, px: number, py = px): SilhouetteMask {
  const cols = Math.max(1, Math.ceil(r.w / px - 1e-9)), rows = Math.max(1, Math.ceil(r.h / py - 1e-9));
  return { x: r.x, y: r.y, px, py, cols, rows, data: new Uint8Array(cols * rows) };
}

/** A `cols` × `rows` mask exactly covering `box` (pixels needn't be square). */
export function gridMask(box: Rect, cols: number, rows: number, data?: Uint8Array): SilhouetteMask {
  cols = Math.max(1, cols);
  rows = Math.max(1, rows);
  return { x: box.x, y: box.y, px: Math.max(box.w, 1e-6) / cols, py: Math.max(box.h, 1e-6) / rows, cols, rows, data: data ?? new Uint8Array(cols * rows) };
}

/** Every pixel set: an opaque box. */
export function boxMask(box: Rect, res = 192): SilhouetteMask {
  const s = res / Math.max(box.w, box.h, 1e-6);
  const m = gridMask(box, Math.ceil(box.w * s), Math.ceil(box.h * s));
  m.data.fill(1);
  return m;
}

/** Flatten path commands into closed polygons (flat [x0,y0,x1,y1…] per contour). */
export function flattenCmds(cmds: ArrayLike<number>, tolerance: number): number[][] {
  const out: number[][] = [];
  for (const c of parseCmds(cmds)) {
    const poly = [c.start.x, c.start.y];
    for (const seg of c.segments) {
      if (seg.kind === "line") {
        poly.push(seg.p1.x, seg.p1.y);
        continue;
      }
      // Control-polygon length bounds the curve length.
      const ctrl = [seg.p0, ...("c0" in seg ? [seg.c0] : []), ...("c1" in seg ? [seg.c1] : []), seg.p1] as Pt[];
      let l = 0;
      for (let k = 1; k < ctrl.length; k++) l += Math.hypot(ctrl[k].x - ctrl[k - 1].x, ctrl[k].y - ctrl[k - 1].y);
      const n = Math.max(2, Math.min(64, Math.ceil(l / Math.max(tolerance, 1e-6))));
      for (let k = 1; k <= n; k++) {
        const p = segmentPoint(seg, k / n);
        poly.push(p.x, p.y);
      }
    }
    if (poly.length >= 6) out.push(poly);
  }
  return out;
}

/**
 * Rasterise filled outlines into a mask (scanline fill at pixel centres, nonzero or even-odd),
 * then stamp every edge so features thinner than a pixel still count.
 */
export function maskFromCmds(cmds: ArrayLike<number>, opts: { box: Rect; evenOdd?: boolean; res?: number }): SilhouetteMask {
  const res = opts.res ?? 192;
  const polys = flattenCmds(cmds, Math.max(opts.box.w, opts.box.h, 1e-6) / res / 2);
  let x0 = opts.box.x, y0 = opts.box.y, x1 = opts.box.x + opts.box.w, y1 = opts.box.y + opts.box.h;
  for (const p of polys)
    for (let k = 0; k < p.length; k += 2) {
      x0 = Math.min(x0, p[k]);
      x1 = Math.max(x1, p[k]);
      y0 = Math.min(y0, p[k + 1]);
      y1 = Math.max(y1, p[k + 1]);
    }
  const px = Math.max(x1 - x0, y1 - y0, 1e-6) / res;
  const m = emptyMask({ x: x0 - px, y: y0 - px, w: x1 - x0 + 2 * px, h: y1 - y0 + 2 * px }, px);
  fillPolys(m, polys, !!opts.evenOdd);
  return m;
}

/** Scanline-fill polygons into a mask, then stamp their edges. */
export function fillPolys(m: SilhouetteMask, polys: number[][], evenOdd: boolean) {
  const xs: { x: number; w: number }[] = [];
  for (let r = 0; r < m.rows; r++) {
    const y = m.y + (r + 0.5) * m.py;
    xs.length = 0;
    for (const p of polys) {
      const n = p.length / 2;
      for (let k = 0; k < n; k++) {
        const ax = p[k * 2], ay = p[k * 2 + 1];
        const bx = p[((k + 1) % n) * 2], by = p[((k + 1) % n) * 2 + 1];
        if (ay <= y && by > y) xs.push({ x: ax + ((y - ay) / (by - ay)) * (bx - ax), w: 1 });
        else if (by <= y && ay > y) xs.push({ x: ax + ((y - ay) / (by - ay)) * (bx - ax), w: -1 });
      }
    }
    xs.sort((a, b) => a.x - b.x);
    let wind = 0;
    for (let k = 0; k < xs.length - 1; k++) {
      wind += xs[k].w;
      const inside = evenOdd ? (k + 1) % 2 === 1 : wind !== 0;
      if (!inside) continue;
      // Pixels whose centres lie in [xs[k], xs[k+1]).
      const c0 = Math.max(0, Math.ceil((xs[k].x - m.x) / m.px - 0.5));
      const c1 = Math.min(m.cols - 1, Math.ceil((xs[k + 1].x - m.x) / m.px - 0.5) - 1);
      for (let c = c0; c <= c1; c++) m.data[r * m.cols + c] = 1;
    }
  }
  const step = Math.min(m.px, m.py) / 2;
  for (const p of polys) {
    const n = p.length / 2;
    for (let k = 0; k < n; k++) {
      const ax = p[k * 2], ay = p[k * 2 + 1];
      const bx = p[((k + 1) % n) * 2], by = p[((k + 1) % n) * 2 + 1];
      const steps = Math.max(1, Math.ceil(Math.hypot(bx - ax, by - ay) / step));
      for (let s = 0; s <= steps; s++) setMaskAt(m, ax + ((bx - ax) * s) / steps, ay + ((by - ay) * s) / steps);
    }
  }
}

function setMaskAt(m: SilhouetteMask, x: number, y: number) {
  const c = Math.floor((x - m.x) / m.px), r = Math.floor((y - m.y) / m.py);
  if (c >= 0 && r >= 0 && c < m.cols && r < m.rows) m.data[r * m.cols + c] = 1;
}

/** The same mask with `pad` local units of empty border on every side. */
export function padMask(m: SilhouetteMask, pad: number): SilhouetteMask {
  const kx = Math.max(0, Math.ceil(pad / m.px)), ky = Math.max(0, Math.ceil(pad / m.py));
  if (!kx && !ky) return m;
  const cols = m.cols + 2 * kx, rows = m.rows + 2 * ky;
  const out: SilhouetteMask = { x: m.x - kx * m.px, y: m.y - ky * m.py, px: m.px, py: m.py, cols, rows, data: new Uint8Array(cols * rows) };
  for (let r = 0; r < m.rows; r++) out.data.set(m.data.subarray(r * m.cols, (r + 1) * m.cols), (r + ky) * cols + kx);
  return out;
}

const FAR = 1e20;

/** Squared Euclidean distance (local units²) from each pixel to the nearest pixel where `set(i)`. */
function distanceField(m: SilhouetteMask, set: (v: number) => boolean): Float64Array {
  const { cols: W, rows: H } = m;
  const d = new Float64Array(W * H);
  for (let i = 0; i < d.length; i++) d[i] = set(m.data[i]) ? 0 : FAR;
  const n = Math.max(W, H);
  const f = new Float64Array(n), g = new Float64Array(n), z = new Float64Array(n + 1);
  const v = new Int32Array(n);
  const pass = (len: number, a: number) => {
    // Felzenszwalb & Huttenlocher's 1-D squared distance transform (weight a per pixel²), f → g.
    let k = 0;
    v[0] = 0;
    z[0] = -FAR;
    z[1] = FAR;
    for (let q = 1; q < len; q++) {
      let s = (f[q] + a * q * q - (f[v[k]] + a * v[k] * v[k])) / (2 * a * (q - v[k]));
      while (s <= z[k]) {
        k--;
        s = (f[q] + a * q * q - (f[v[k]] + a * v[k] * v[k])) / (2 * a * (q - v[k]));
      }
      k++;
      v[k] = q;
      z[k] = s;
      z[k + 1] = FAR;
    }
    k = 0;
    for (let q = 0; q < len; q++) {
      while (z[k + 1] < q) k++;
      g[q] = a * (q - v[k]) * (q - v[k]) + f[v[k]];
    }
  };
  for (let x = 0; x < W; x++) {
    for (let y = 0; y < H; y++) f[y] = d[y * W + x];
    pass(H, m.py * m.py);
    for (let y = 0; y < H; y++) d[y * W + x] = g[y];
  }
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) f[x] = d[y * W + x];
    pass(W, m.px * m.px);
    for (let x = 0; x < W; x++) d[y * W + x] = g[x];
  }
  return d;
}

/** Grow (r > 0) or shrink (r < 0) the mask by |r| local units, in place. */
export function dilateMask(m: SilhouetteMask, r: number) {
  if (Math.abs(r) < Math.min(m.px, m.py) * 0.5) return;
  const rr = r * r;
  if (r > 0) {
    const d = distanceField(m, (v) => v !== 0);
    for (let i = 0; i < d.length; i++) if (d[i] <= rr) m.data[i] = 1;
  } else {
    const d = distanceField(m, (v) => v === 0);
    for (let i = 0; i < d.length; i++) if (d[i] <= rr) m.data[i] = 0;
  }
}

// ------------------------------------------------------------------ triangulation

/**
 * A triangle mesh over a silhouette, built on a regular grid (cells split along alternating
 * diagonals) so points can be located in O(1). Vertices are numbered row by row, which keeps
 * the solver's matrices narrow-banded.
 */
export interface PuppetMesh {
  /** Rest positions, node-local [x0, y0, x1, y1…]. */
  rest: Float64Array;
  /** Vertex indices, three per triangle, counter-clockwise on screen (y down). */
  tris: Uint32Array;
  vertexCount: number;
  triCount: number;
  /** Grid: cell (i, j) spans [ox + i·cw, ox + (i+1)·cw] × [oy + j·ch, …]. */
  ox: number;
  oy: number;
  cw: number;
  ch: number;
  nx: number;
  ny: number;
  /** First triangle of each cell (two per cell), or -1 outside the mesh. */
  cellTri: Int32Array;
  /** Nearest cell inside the mesh, for every cell (extrapolation outside the silhouette). */
  nearCell: Int32Array;
  /** Connected component of each vertex. */
  comp: Int32Array;
  compCount: number;
}

export interface PuppetMeshOptions {
  density: PuppetDensity;
  /** Grow (or shrink, if negative) the silhouette by this many local units first. */
  expansion: number;
  /** Morphological closing radius: bridges gaps narrower than 2·close (letters into words). */
  close?: number;
  /** Grid lines pass through this point, so an opaque box meshes exactly onto its edges. */
  align?: Rect;
  /** Override the vertex budget (tests). */
  target?: number;
}

export function buildPuppetMesh(mask: SilhouetteMask, opts: PuppetMeshOptions): PuppetMesh {
  const close = Math.max(0, opts.close ?? 0);
  const m = padMask(mask, Math.max(0, opts.expansion) + close + 2 * Math.max(mask.px, mask.py));
  if (close > 0) {
    dilateMask(m, close);
    dilateMask(m, -close);
  }
  dilateMask(m, opts.expansion);
  let count = 0;
  for (let i = 0; i < m.data.length; i++) count += m.data[i];
  const area = Math.max(count, 1) * m.px * m.py;
  const target = opts.target ?? DENSITY_TARGET[opts.density];
  let cell = Math.max(m.px, m.py, Math.sqrt(area / target));
  const al = opts.align;
  let cw = cell, ch = cell, ox = m.x, oy = m.y;
  if (al && al.w > 0 && al.h > 0) {
    cw = al.w / Math.max(1, Math.round(al.w / cell));
    ch = al.h / Math.max(1, Math.round(al.h / cell));
    ox = al.x;
    oy = al.y;
  }
  // Cell range covering the mask.
  const i0 = Math.floor((m.x - ox) / cw), j0 = Math.floor((m.y - oy) / ch);
  const i1 = Math.floor((m.x + m.cols * m.px - ox) / cw), j1 = Math.floor((m.y + m.rows * m.py - oy) / ch);
  ox += i0 * cw;
  oy += j0 * ch;
  const nx = i1 - i0 + 1, ny = j1 - j0 + 1;
  const inCell = new Uint8Array(nx * ny);
  for (let r = 0; r < m.rows; r++) {
    const y = m.y + (r + 0.5) * m.py;
    const j = Math.min(ny - 1, Math.max(0, Math.floor((y - oy) / ch)));
    for (let c = 0; c < m.cols; c++) {
      if (!m.data[r * m.cols + c]) continue;
      const x = m.x + (c + 0.5) * m.px;
      const i = Math.min(nx - 1, Math.max(0, Math.floor((x - ox) / cw)));
      inCell[j * nx + i] = 1;
    }
  }
  // Vertices: corners of included cells, numbered row-major.
  const vid = new Int32Array((nx + 1) * (ny + 1)).fill(-1);
  for (let j = 0; j < ny; j++)
    for (let i = 0; i < nx; i++) {
      if (!inCell[j * nx + i]) continue;
      for (const [a, b] of [[0, 0], [1, 0], [0, 1], [1, 1]]) vid[(j + b) * (nx + 1) + i + a] = 0;
    }
  const restArr: number[] = [];
  let nv = 0;
  for (let j = 0; j <= ny; j++)
    for (let i = 0; i <= nx; i++) {
      const k = j * (nx + 1) + i;
      if (vid[k] < 0) continue;
      vid[k] = nv++;
      restArr.push(ox + i * cw, oy + j * ch);
    }
  const tris: number[] = [];
  const cellTri = new Int32Array(nx * ny).fill(-1);
  for (let j = 0; j < ny; j++)
    for (let i = 0; i < nx; i++) {
      if (!inCell[j * nx + i]) continue;
      const a = vid[j * (nx + 1) + i], b = vid[j * (nx + 1) + i + 1];
      const c = vid[(j + 1) * (nx + 1) + i], d = vid[(j + 1) * (nx + 1) + i + 1];
      cellTri[j * nx + i] = tris.length / 3;
      if ((i + j) % 2 === 0) tris.push(a, b, d, a, d, c);
      else tris.push(a, b, c, b, d, c);
    }
  // Nearest included cell for every cell (multi-source BFS, 8-connected).
  const nearCell = new Int32Array(nx * ny).fill(-1);
  const queue: number[] = [];
  for (let k = 0; k < nx * ny; k++)
    if (inCell[k]) {
      nearCell[k] = k;
      queue.push(k);
    }
  for (let h = 0; h < queue.length; h++) {
    const k = queue[h], i = k % nx, j = (k / nx) | 0;
    for (let b = -1; b <= 1; b++)
      for (let a = -1; a <= 1; a++) {
        const ii = i + a, jj = j + b;
        if (ii < 0 || jj < 0 || ii >= nx || jj >= ny) continue;
        const q = jj * nx + ii;
        if (nearCell[q] >= 0) continue;
        nearCell[q] = nearCell[k];
        queue.push(q);
      }
  }
  // Components (union-find over triangle edges).
  const parent = new Int32Array(nv);
  for (let k = 0; k < nv; k++) parent[k] = k;
  const find = (k: number): number => {
    while (parent[k] !== k) k = parent[k] = parent[parent[k]];
    return k;
  };
  for (let t = 0; t < tris.length; t += 3) {
    const r = find(tris[t]);
    parent[find(tris[t + 1])] = r;
    parent[find(tris[t + 2])] = r;
  }
  const comp = new Int32Array(nv);
  const label = new Map<number, number>();
  for (let k = 0; k < nv; k++) {
    const r = find(k);
    let l = label.get(r);
    if (l === undefined) label.set(r, (l = label.size));
    comp[k] = l;
  }
  const mesh: PuppetMesh = {
    rest: Float64Array.from(restArr),
    tris: Uint32Array.from(tris),
    vertexCount: nv,
    triCount: tris.length / 3,
    ox, oy, cw, ch, nx, ny, cellTri, nearCell, comp, compCount: label.size,
  };
  hugContour(mesh, m);
  return mesh;
}

/**
 * The grid's boundary is a staircase around the silhouette. Pull each boundary vertex in
 * along the distance field until it sits on the (grown) silhouette's edge, so the mesh hugs
 * the shape; moves are capped and undone if they would flip a triangle.
 */
function hugContour(mesh: PuppetMesh, m: SilhouetteMask) {
  const d2 = distanceField(m, (v) => v !== 0);
  const D = (x: number, y: number) => {
    const fx = Math.min(m.cols - 1, Math.max(0, (x - m.x) / m.px - 0.5)), fy = Math.min(m.rows - 1, Math.max(0, (y - m.y) / m.py - 0.5));
    const x0 = Math.floor(fx), y0 = Math.floor(fy), x1 = Math.min(m.cols - 1, x0 + 1), y1 = Math.min(m.rows - 1, y0 + 1);
    const tx = fx - x0, ty = fy - y0;
    const at = (c: number, r: number) => Math.sqrt(d2[r * m.cols + c]);
    return (at(x0, y0) * (1 - tx) + at(x1, y0) * tx) * (1 - ty) + (at(x0, y1) * (1 - tx) + at(x1, y1) * tx) * ty;
  };
  const target = Math.max(m.px, m.py);
  const cap = 0.6 * Math.min(mesh.cw, mesh.ch);
  const h = Math.min(m.px, m.py);
  const loops = meshBoundary(mesh);
  const incident: number[][] = Array.from({ length: mesh.vertexCount }, () => []);
  for (let t = 0; t < mesh.triCount; t++) for (let e = 0; e < 3; e++) incident[mesh.tris[t * 3 + e]].push(t);
  const R = mesh.rest;
  const minArea = 0.05 * mesh.cw * mesh.ch;
  const ok = (v: number) =>
    incident[v].every((t) => {
      const a = mesh.tris[t * 3], b = mesh.tris[t * 3 + 1], c = mesh.tris[t * 3 + 2];
      return (R[b * 2] - R[a * 2]) * (R[c * 2 + 1] - R[a * 2 + 1]) - (R[c * 2] - R[a * 2]) * (R[b * 2 + 1] - R[a * 2 + 1]) > minArea;
    });
  for (const loop of loops)
    for (const v of loop) {
      const x0 = R[v * 2], y0 = R[v * 2 + 1];
      let x = x0, y = y0;
      for (let it = 0; it < 4; it++) {
        const d = D(x, y);
        if (d <= target) break;
        const gx = (D(x + h, y) - D(x - h, y)) / (2 * h), gy = (D(x, y + h) - D(x, y - h)) / (2 * h);
        const g = Math.hypot(gx, gy);
        if (g < 1e-6) break;
        x -= ((d - target) * gx) / (g * g);
        y -= ((d - target) * gy) / (g * g);
      }
      const mv = Math.hypot(x - x0, y - y0);
      if (mv > cap) {
        x = x0 + ((x - x0) * cap) / mv;
        y = y0 + ((y - y0) * cap) / mv;
      }
      R[v * 2] = x;
      R[v * 2 + 1] = y;
      if (!ok(v)) {
        R[v * 2] = x0;
        R[v * 2 + 1] = y0;
      }
    }
}

/**
 * The triangle (of the rest mesh) to use for a rest-space point and its barycentric
 * coordinates. Points outside the silhouette use the nearest cell's triangle, extrapolated.
 */
export function locateRest(mesh: PuppetMesh, x: number, y: number): { tri: number; b: [number, number, number]; inside: boolean } | null {
  if (!mesh.triCount) return null;
  const fi = (x - mesh.ox) / mesh.cw, fj = (y - mesh.oy) / mesh.ch;
  const ci = Math.min(mesh.nx - 1, Math.max(0, Math.floor(fi))), cj = Math.min(mesh.ny - 1, Math.max(0, Math.floor(fj)));
  let cell = cj * mesh.nx + ci;
  const inCell = mesh.cellTri[cell] >= 0 && fi >= 0 && fj >= 0 && fi <= mesh.nx && fj <= mesh.ny;
  cell = mesh.nearCell[cell];
  if (cell < 0) return null;
  const i = cell % mesh.nx, j = (cell / mesh.nx) | 0;
  const u = fi - i, v = fj - j;
  const t0 = mesh.cellTri[cell];
  // Which half of the cell (see the diagonal choice in buildPuppetMesh). Boundary vertices
  // have been pulled in, so check the other half too.
  const second = (i + j) % 2 === 0 ? v > u : u + v > 1;
  let tri = t0 + (second ? 1 : 0);
  let b = barycentric(mesh.rest, mesh.tris, tri, x, y);
  if (Math.min(...b) < -1e-9) {
    const alt = barycentric(mesh.rest, mesh.tris, t0 + (second ? 0 : 1), x, y);
    if (Math.min(...alt) > Math.min(...b)) {
      tri = t0 + (second ? 0 : 1);
      b = alt;
    }
  }
  return { tri, b, inside: inCell && Math.min(...b) >= -1e-6 };
}

function barycentric(pos: ArrayLike<number>, tris: Uint32Array, t: number, x: number, y: number): [number, number, number] {
  const a = tris[t * 3], b = tris[t * 3 + 1], c = tris[t * 3 + 2];
  const ax = pos[a * 2], ay = pos[a * 2 + 1];
  const e1x = pos[b * 2] - ax, e1y = pos[b * 2 + 1] - ay, e2x = pos[c * 2] - ax, e2y = pos[c * 2 + 1] - ay;
  const det = e1x * e2y - e1y * e2x;
  if (Math.abs(det) < 1e-18) return [1, 0, 0];
  const px = x - ax, py = y - ay;
  const s = (px * e2y - py * e2x) / det, r = (e1x * py - e1y * px) / det;
  return [1 - s - r, s, r];
}

/**
 * Inverse lookup: the rest-space point shown at output point (x, y) of the deformed mesh
 * (top-most triangle in draw order), or null if the point isn't on the mesh.
 */
export function locateDeformed(mesh: PuppetMesh, pos: ArrayLike<number>, order: ArrayLike<number> | null, x: number, y: number): Pt | null {
  const n = mesh.triCount;
  for (let k = n - 1; k >= 0; k--) {
    const t = order ? order[k] : k;
    const [b0, b1, b2] = barycentric(pos, mesh.tris, t, x, y);
    const e = -1e-9;
    if (b0 < e || b1 < e || b2 < e) continue;
    const a = mesh.tris[t * 3], b = mesh.tris[t * 3 + 1], c = mesh.tris[t * 3 + 2];
    const R = mesh.rest;
    return { x: b0 * R[a * 2] + b1 * R[b * 2] + b2 * R[c * 2], y: b0 * R[a * 2 + 1] + b1 * R[b * 2 + 1] + b2 * R[c * 2 + 1] };
  }
  return null;
}

/** Closed boundary loops of the mesh (vertex indices), counter-clockwise for outer edges. */
export function meshBoundary(mesh: PuppetMesh): number[][] {
  const key = (a: number, b: number) => a * mesh.vertexCount + b;
  const edges = new Set<number>();
  for (let t = 0; t < mesh.tris.length; t += 3)
    for (let e = 0; e < 3; e++) edges.add(key(mesh.tris[t + e], mesh.tris[t + ((e + 1) % 3)]));
  const next = new Map<number, number[]>();
  for (let t = 0; t < mesh.tris.length; t += 3)
    for (let e = 0; e < 3; e++) {
      const a = mesh.tris[t + e], b = mesh.tris[t + ((e + 1) % 3)];
      if (edges.has(key(b, a))) continue;
      const l = next.get(a);
      if (l) l.push(b);
      else next.set(a, [b]);
    }
  const loops: number[][] = [];
  for (const start of [...next.keys()]) {
    while (next.get(start)?.length) {
      const loop = [start];
      let at = next.get(start)!.pop()!;
      while (at !== start) {
        loop.push(at);
        const l = next.get(at);
        if (!l?.length) break;
        at = l.pop()!;
      }
      if (loop.length >= 3) loops.push(loop);
    }
  }
  return loops;
}

/** Boundary loops as closed path commands through vertex positions `pos`. */
export function boundaryCmds(loops: number[][], pos: ArrayLike<number>): number[] {
  const out: number[] = [];
  for (const loop of loops) {
    loop.forEach((v, k) => out.push(k ? VERB.LINE : VERB.MOVE, pos[v * 2], pos[v * 2 + 1]));
    out.push(VERB.CLOSE);
  }
  return out;
}

// ------------------------------------------------------------------ sparse Cholesky

/**
 * Symmetric positive-definite matrix in skyline (envelope) storage: row i keeps columns
 * first[i]..i. Cholesky fills in only inside the envelope, so a row-major grid mesh factors
 * in O(n · band²) and solves in O(n · band).
 */
export class Skyline {
  private start: Int32Array;
  readonly val: Float64Array;
  constructor(
    readonly n: number,
    readonly first: Int32Array,
  ) {
    this.start = new Int32Array(n + 1);
    for (let i = 0; i < n; i++) this.start[i + 1] = this.start[i] + (i - first[i] + 1);
    this.val = new Float64Array(this.start[n]);
  }

  add(i: number, j: number, v: number) {
    if (j > i) [i, j] = [j, i];
    this.val[this.start[i] + j - this.first[i]] += v;
  }

  /** In-place LLᵀ factorisation. */
  factor() {
    const { n, first, start, val } = this;
    for (let i = 0; i < n; i++) {
      const fi = first[i], si = start[i] - fi;
      for (let j = fi; j <= i; j++) {
        const fj = first[j], sj = start[j] - fj;
        let s = val[si + j];
        for (let k = Math.max(fi, fj); k < j; k++) s -= val[si + k] * val[sj + k];
        if (j < i) val[si + j] = s / val[sj + j];
        else val[si + i] = Math.sqrt(Math.max(s, 1e-12));
      }
    }
  }

  /** Solve LLᵀx = b in place. */
  solve(b: Float64Array) {
    const { n, first, start, val } = this;
    for (let i = 0; i < n; i++) {
      const fi = first[i], si = start[i] - fi;
      let s = b[i];
      for (let k = fi; k < i; k++) s -= val[si + k] * b[k];
      b[i] = s / val[si + i];
    }
    for (let i = n - 1; i >= 0; i--) {
      const fi = first[i], si = start[i] - fi;
      const x = (b[i] /= val[si + i]);
      for (let k = fi; k < i; k++) b[k] -= val[si + k] * x;
    }
  }
}

// ------------------------------------------------------------------ ARAP solver

export interface PuppetPinRest {
  rest: readonly [number, number];
  /** Fixed rotation (the pin holds its neighbourhood at an angle) vs automatic. */
  fixed: boolean;
}

export interface PuppetPinTarget {
  pos: readonly [number, number];
  /** Degrees, null = automatic. */
  rotation: number | null;
}

/** A linear constraint: Σ w·v[vert] ≈ the pin's position (+ rotated offset, for fixed pins). */
interface Constraint {
  verts: number[];
  w: number[];
  pin: number;
  /** Rest offset from the pin, rotated by the pin's angle (fixed pins only). */
  offset: [number, number] | null;
}

interface Component {
  verts: Int32Array;
  kind: "rest" | "translate" | "solve";
  cons: Constraint[];
  /** Local index of every global vertex in this component. */
  local?: Map<number, number>;
  tris?: Int32Array;
  A1?: Skyline;
  A2?: Skyline;
}

const PENALTY = 1e5;
/** Add entry (r, c) of a full symmetric matrix: only the stored (lower) half is kept. */
const lower = (A: Skyline, r: number, c: number, v: number) => {
  if (r >= c) A.add(r, c, v);
};
const REG = 1e-8;

/**
 * As-rigid-as-possible deformation of a puppet mesh. Construct once per (mesh, pin set,
 * fixed-rotation flags); `solve` then runs per drag frame.
 */
export class PuppetSolver {
  private comps: Component[] = [];

  constructor(
    readonly mesh: PuppetMesh,
    pins: readonly PuppetPinRest[],
  ) {
    const cons: Constraint[] = [];
    pins.forEach((p, k) => {
      const loc = locateRest(mesh, p.rest[0], p.rest[1]);
      if (!loc) return;
      const t = loc.tri;
      const vs = [mesh.tris[t * 3], mesh.tris[t * 3 + 1], mesh.tris[t * 3 + 2]];
      if (p.fixed) {
        // Hold the pin's whole triangle rigidly at the pin's angle.
        for (const v of vs) cons.push({ verts: [v], w: [1], pin: k, offset: [mesh.rest[v * 2] - p.rest[0], mesh.rest[v * 2 + 1] - p.rest[1]] });
      } else cons.push({ verts: vs, w: loc.b, pin: k, offset: null });
    });
    const byComp: number[][] = Array.from({ length: mesh.compCount }, () => []);
    for (let v = 0; v < mesh.vertexCount; v++) byComp[mesh.comp[v]].push(v);
    const trisByComp: number[][] = Array.from({ length: mesh.compCount }, () => []);
    for (let t = 0; t < mesh.triCount; t++) trisByComp[mesh.comp[mesh.tris[t * 3]]].push(t);
    for (let c = 0; c < mesh.compCount; c++) {
      const verts = Int32Array.from(byComp[c]);
      const cc = cons.filter((k) => mesh.comp[k.verts[0]] === c);
      const pinsHere = new Set(cc.map((k) => k.pin));
      const kind = !cc.length ? "rest" : pinsHere.size === 1 && cc.length === 1 ? "translate" : "solve";
      const comp: Component = { verts, kind, cons: cc };
      if (kind === "solve") this.prepare(comp, Int32Array.from(trisByComp[c]));
      this.comps.push(comp);
    }
  }

  private prepare(comp: Component, tris: Int32Array) {
    const mesh = this.mesh;
    const local = new Map<number, number>();
    comp.verts.forEach((v, k) => local.set(v, k));
    const n = comp.verts.length;
    // Envelope: lowest-numbered neighbour of every vertex (constraints couple their vertices too).
    const low = new Int32Array(n);
    for (let k = 0; k < n; k++) low[k] = k;
    const couple = (ids: number[]) => {
      let m = Infinity;
      for (const v of ids) m = Math.min(m, v);
      for (const v of ids) low[v] = Math.min(low[v], m);
    };
    for (const t of tris) couple([local.get(mesh.tris[t * 3])!, local.get(mesh.tris[t * 3 + 1])!, local.get(mesh.tris[t * 3 + 2])!]);
    for (const c of comp.cons) couple(c.verts.map((v) => local.get(v)!));
    const first2 = new Int32Array(2 * n);
    for (let k = 0; k < n; k++) first2[2 * k] = first2[2 * k + 1] = 2 * low[k];
    const A1 = new Skyline(2 * n, first2);
    const A2 = new Skyline(n, low);
    const R = mesh.rest;
    for (const t of tris) {
      const ids = [local.get(mesh.tris[t * 3])!, local.get(mesh.tris[t * 3 + 1])!, local.get(mesh.tris[t * 3 + 2])!];
      const g = [mesh.tris[t * 3], mesh.tris[t * 3 + 1], mesh.tris[t * 3 + 2]];
      for (let e = 0; e < 3; e++) {
        const i0 = e, i1 = (e + 1) % 3, i2 = (e + 2) % 3;
        const dx = R[g[i1] * 2] - R[g[i0] * 2], dy = R[g[i1] * 2 + 1] - R[g[i0] * 2 + 1];
        const ex = R[g[i2] * 2] - R[g[i0] * 2], ey = R[g[i2] * 2 + 1] - R[g[i0] * 2 + 1];
        const d2 = dx * dx + dy * dy || 1e-18;
        // v2 = v0 + x·d + y·R90·d, R90 = [[0,-1],[1,0]].
        const x = (ex * dx + ey * dy) / d2, y = (ey * dx - ex * dy) / d2;
        // Residual r = C0·v0 + C1·v1 + C2·v2 with 2×2 blocks.
        const C: [number, number, number, number][] = [
          [x - 1, -y, y, x - 1],
          [-x, y, -y, -x],
          [1, 0, 0, 1],
        ];
        const vi = [ids[i0], ids[i1], ids[i2]];
        for (let a = 0; a < 3; a++)
          for (let b = 0; b < 3; b++) {
            const Ca = C[a], Cb = C[b];
            // Block (a, b) of the full matrix is Caᵀ·Cb (row-major 2×2 [m00, m01, m10, m11]).
            const blk = [Ca[0] * Cb[0] + Ca[2] * Cb[2], Ca[0] * Cb[1] + Ca[2] * Cb[3], Ca[1] * Cb[0] + Ca[3] * Cb[2], Ca[1] * Cb[1] + Ca[3] * Cb[3]];
            for (let p = 0; p < 2; p++) for (let q = 0; q < 2; q++) lower(A1, 2 * vi[a] + p, 2 * vi[b] + q, blk[p * 2 + q]);
          }
        // Step 2: edge (i0, i1) of this triangle.
        A2.add(ids[i0], ids[i0], 1);
        A2.add(ids[i1], ids[i1], 1);
        A2.add(ids[i1], ids[i0], -1);
      }
    }
    for (const c of comp.cons) {
      const li = c.verts.map((v) => local.get(v)!);
      for (let a = 0; a < li.length; a++)
        for (let b = 0; b < li.length; b++) {
          const w = PENALTY * c.w[a] * c.w[b];
          lower(A1, 2 * li[a], 2 * li[b], w);
          lower(A1, 2 * li[a] + 1, 2 * li[b] + 1, w);
          lower(A2, li[a], li[b], w);
        }
    }
    for (let k = 0; k < 2 * n; k++) A1.add(k, k, REG);
    for (let k = 0; k < n; k++) A2.add(k, k, REG);
    A1.factor();
    A2.factor();
    Object.assign(comp, { local, tris, A1, A2 });
  }

  /** Deformed vertex positions for the pins' current targets. */
  solve(targets: readonly PuppetPinTarget[], mode: PuppetMode): Float64Array {
    const mesh = this.mesh;
    const R = mesh.rest;
    const out = new Float64Array(R);
    const goal = (c: Constraint): [number, number] => {
      const t = targets[c.pin];
      if (!c.offset) return [t.pos[0], t.pos[1]];
      const a = ((t.rotation ?? 0) * Math.PI) / 180, co = Math.cos(a), si = Math.sin(a);
      return [t.pos[0] + co * c.offset[0] - si * c.offset[1], t.pos[1] + si * c.offset[0] + co * c.offset[1]];
    };
    for (const comp of this.comps) {
      if (comp.kind === "rest") continue;
      if (comp.kind === "translate") {
        const c = comp.cons[0];
        let rx = 0, ry = 0;
        c.verts.forEach((v, k) => {
          rx += c.w[k] * R[v * 2];
          ry += c.w[k] * R[v * 2 + 1];
        });
        const [gx, gy] = goal(c);
        for (const v of comp.verts) {
          out[v * 2] += gx - rx;
          out[v * 2 + 1] += gy - ry;
        }
        continue;
      }
      this.solveComponent(comp, goal, MODE_SCALE[mode], out);
    }
    return out;
  }

  private solveComponent(comp: Component, goal: (c: Constraint) => [number, number], scaleKeep: number, out: Float64Array) {
    const mesh = this.mesh;
    const R = mesh.rest;
    const { verts, local, tris, A1, A2, cons } = comp as Required<Component>;
    const n = verts.length;
    // Step 1: similarity-invariant energy (rotation and uniform scale are free).
    const b1 = new Float64Array(2 * n);
    for (let k = 0; k < n; k++) {
      b1[2 * k] = REG * R[verts[k] * 2];
      b1[2 * k + 1] = REG * R[verts[k] * 2 + 1];
    }
    const goals = cons.map(goal);
    cons.forEach((c, ci) => {
      const [gx, gy] = goals[ci];
      c.verts.forEach((v, a) => {
        const li = local.get(v)!;
        b1[2 * li] += PENALTY * c.w[a] * gx;
        b1[2 * li + 1] += PENALTY * c.w[a] * gy;
      });
    });
    A1.solve(b1);
    // Step 2: fit each rest triangle (rotation, plus some of its scale) to the step-1 result,
    // then find positions whose edges best match the fitted triangles.
    const bx = new Float64Array(n), by = new Float64Array(n);
    for (let k = 0; k < n; k++) {
      bx[k] = REG * R[verts[k] * 2];
      by[k] = REG * R[verts[k] * 2 + 1];
    }
    const fx = [0, 0, 0], fy = [0, 0, 0];
    for (const t of tris) {
      const ids = [local.get(mesh.tris[t * 3])!, local.get(mesh.tris[t * 3 + 1])!, local.get(mesh.tris[t * 3 + 2])!];
      const g = [mesh.tris[t * 3], mesh.tris[t * 3 + 1], mesh.tris[t * 3 + 2]];
      let cqx = 0, cqy = 0, cpx = 0, cpy = 0;
      for (let k = 0; k < 3; k++) {
        cqx += R[g[k] * 2] / 3;
        cqy += R[g[k] * 2 + 1] / 3;
        cpx += b1[2 * ids[k]] / 3;
        cpy += b1[2 * ids[k] + 1] / 3;
      }
      let sd = 0, sc = 0, sa = 0;
      for (let k = 0; k < 3; k++) {
        const ax = R[g[k] * 2] - cqx, ay = R[g[k] * 2 + 1] - cqy;
        const px = b1[2 * ids[k]] - cpx, py = b1[2 * ids[k] + 1] - cpy;
        sd += ax * px + ay * py;
        sc += ax * py - ay * px;
        sa += ax * ax + ay * ay;
      }
      const ang = Math.atan2(sc, sd);
      const s = sa > 1e-18 ? Math.pow(Math.max(1e-6, Math.hypot(sd, sc) / sa), scaleKeep) : 1;
      const co = Math.cos(ang) * s, si = Math.sin(ang) * s;
      for (let k = 0; k < 3; k++) {
        const ax = R[g[k] * 2] - cqx, ay = R[g[k] * 2 + 1] - cqy;
        fx[k] = co * ax - si * ay;
        fy[k] = si * ax + co * ay;
      }
      for (let e = 0; e < 3; e++) {
        const i = e, j = (e + 1) % 3;
        const ex = fx[i] - fx[j], ey = fy[i] - fy[j];
        bx[ids[i]] += ex;
        by[ids[i]] += ey;
        bx[ids[j]] -= ex;
        by[ids[j]] -= ey;
      }
    }
    cons.forEach((c, ci) => {
      const [gx, gy] = goals[ci];
      c.verts.forEach((v, a) => {
        const li = local.get(v)!;
        bx[li] += PENALTY * c.w[a] * gx;
        by[li] += PENALTY * c.w[a] * gy;
      });
    });
    A2.solve(bx);
    A2.solve(by);
    for (let k = 0; k < n; k++) {
      out[verts[k] * 2] = bx[k];
      out[verts[k] * 2 + 1] = by[k];
    }
  }
}

// ------------------------------------------------------------------ deformed mesh

/** Per-triangle affine maps rest → deformed: [a, b, c, d, tx, ty] with p' = [a b; c d]·p + t. */
export function triangleAffines(mesh: PuppetMesh, pos: ArrayLike<number>): Float64Array {
  const out = new Float64Array(mesh.triCount * 6);
  const R = mesh.rest;
  for (let t = 0; t < mesh.triCount; t++) {
    const a = mesh.tris[t * 3], b = mesh.tris[t * 3 + 1], c = mesh.tris[t * 3 + 2];
    const q1x = R[b * 2] - R[a * 2], q1y = R[b * 2 + 1] - R[a * 2 + 1], q2x = R[c * 2] - R[a * 2], q2y = R[c * 2 + 1] - R[a * 2 + 1];
    const p1x = pos[b * 2] - pos[a * 2], p1y = pos[b * 2 + 1] - pos[a * 2 + 1], p2x = pos[c * 2] - pos[a * 2], p2y = pos[c * 2 + 1] - pos[a * 2 + 1];
    const det = q1x * q2y - q2x * q1y || 1e-18;
    // M = P·Q⁻¹, Q⁻¹ = [q2y -q2x; -q1y q1x] / det.
    const m00 = (p1x * q2y - p2x * q1y) / det, m01 = (-p1x * q2x + p2x * q1x) / det;
    const m10 = (p1y * q2y - p2y * q1y) / det, m11 = (-p1y * q2x + p2y * q1x) / det;
    out.set([m00, m01, m10, m11, pos[a * 2] - (m00 * R[a * 2] + m01 * R[a * 2 + 1]), pos[a * 2 + 1] - (m10 * R[a * 2] + m11 * R[a * 2 + 1])], t * 6);
  }
  return out;
}

/** Piecewise-affine point map through the deformed mesh (extrapolated outside it). */
export function puppetWarpFn(mesh: PuppetMesh, affines: Float64Array): WarpFn {
  return (x, y) => {
    const loc = locateRest(mesh, x, y);
    if (!loc) return { x, y, j: [1, 0, 0, 1] };
    const k = loc.tri * 6;
    const a = affines[k], b = affines[k + 1], c = affines[k + 2], d = affines[k + 3];
    return { x: a * x + b * y + affines[k + 4], y: c * x + d * y + affines[k + 5], j: [a, b, c, d] };
  };
}

/** Rotation (degrees) of the deformed mesh at a rest point: what an automatic pin is turned by. */
export function rotationAt(mesh: PuppetMesh, affines: Float64Array, x: number, y: number): number {
  const loc = locateRest(mesh, x, y);
  if (!loc) return 0;
  const k = loc.tri * 6;
  return (Math.atan2(affines[k + 2] - affines[k + 1], affines[k] + affines[k + 3]) * 180) / Math.PI;
}

/**
 * Draw order of the triangles: each takes the depth of its nearest pin (rest space), and
 * deeper-numbered pins draw on top where folded parts overlap. Stable within a depth.
 */
export function triangleOrder(mesh: PuppetMesh, pins: readonly { rest: readonly [number, number]; depth: number }[]): Uint32Array {
  const order = new Uint32Array(mesh.triCount);
  for (let t = 0; t < mesh.triCount; t++) order[t] = t;
  if (pins.length < 2 || pins.every((p) => p.depth === pins[0].depth)) return order;
  const depth = new Float64Array(mesh.triCount);
  const R = mesh.rest;
  for (let t = 0; t < mesh.triCount; t++) {
    const a = mesh.tris[t * 3], b = mesh.tris[t * 3 + 1], c = mesh.tris[t * 3 + 2];
    const cx = (R[a * 2] + R[b * 2] + R[c * 2]) / 3, cy = (R[a * 2 + 1] + R[b * 2 + 1] + R[c * 2 + 1]) / 3;
    let best = Infinity;
    for (const p of pins) {
      const d = (p.rest[0] - cx) ** 2 + (p.rest[1] - cy) ** 2;
      if (d < best) {
        best = d;
        depth[t] = p.depth;
      }
    }
  }
  return order.sort((x, y) => depth[x] - depth[y] || x - y);
}

export function positionsBounds(pos: ArrayLike<number>): Rect {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (let k = 0; k < pos.length; k += 2) {
    x0 = Math.min(x0, pos[k]);
    x1 = Math.max(x1, pos[k]);
    y0 = Math.min(y0, pos[k + 1]);
    y1 = Math.max(y1, pos[k + 1]);
  }
  return x0 <= x1 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : { x: 0, y: 0, w: 0, h: 0 };
}

/**
 * Rough bounds of a puppet-warped box before its mesh exists (the engine registers exact ones
 * once it has solved): the box moved along with each pin.
 */
export function estimatePuppetBounds(base: Rect, pins: readonly { rest: readonly [number, number]; pos: readonly [number, number] }[]): Rect {
  let x0 = base.x, y0 = base.y, x1 = base.x + base.w, y1 = base.y + base.h;
  for (const p of pins) {
    const dx = p.pos[0] - p.rest[0], dy = p.pos[1] - p.rest[1];
    x0 = Math.min(x0, base.x + dx);
    y0 = Math.min(y0, base.y + dy);
    x1 = Math.max(x1, base.x + base.w + dx);
    y1 = Math.max(y1, base.y + base.h + dy);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
